/* eslint-disable react/no-multi-comp, max-lines */
import React, { useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';
import { Link } from 'react-router-dom';
import { Button, Input, ProfilePicture, Select, Toggle, Tooltip } from '@getro/rombo';
import { Download, HelpCircle, Link as LinkIcon, Search } from 'lucide-react';

import { displayProjectName } from './projectAnalyticsCsv';

const ALL_COMPANIES = 'all';
const COMPANY_UNAVAILABLE = 'company-unavailable';
const EMPTY_VALUE = '—';

const COLUMN_DEFINITIONS = [
  {
    key: 'contacts',
    label: 'Contacted',
    tooltip:
      'Candidates this project sent at least one outreach email to. Someone contacted by two projects is counted in both.',
  },
  {
    key: 'anyLinkCtr',
    label: 'Any-link CTR',
    tooltip: 'Share of contacted candidates who clicked at least one link in an outreach email.',
  },
  {
    key: 'uniqueViewers',
    label: 'Job visitors',
    tooltip:
      'Unique visitors to the linked job on your board, from all sources — outreach, search, social, direct. Not only from this project.',
  },
  {
    key: 'applyClicks',
    label: 'Applies',
    tooltip: 'Clicks on the job’s apply button. Intent to apply; it does not confirm a completed application.',
  },
];

const companyKey = (company) => {
  if (company?.id !== undefined && company?.id !== null) return `company-${company.id}`;
  const normalizedName = company?.name?.trim().toLocaleLowerCase();
  return normalizedName ? `company-name-${normalizedName}` : COMPANY_UNAVAILABLE;
};

const LINKED_LABELS = { exact: 'linked automatically', auto: 'linked from your outreach emails' };
const linkedLabel = (mappingSource) => LINKED_LABELS[mappingSource] || 'linked manually';
const compareLabels = (left, right) => left.label.localeCompare(right.label);

const pluralize = (count, singular, plural = `${singular}s`) => `${count} ${count === 1 ? singular : plural}`;

export const DataState = ({ title, children, action = null, variant = 'default' }) => (
  <div className={`TalentAttraction__data-state TalentAttraction__data-state--${variant}`}>
    <h2>{title}</h2>
    <p>{children}</p>
    {action}
  </div>
);

export const OutreachJourney = ({ summary, projects, hasOutreachData, hasJobData, rangeLabel, formatNumber }) => {
  const applyRate = summary.uniqueJobViewers ? summary.jobApplyClicks / summary.uniqueJobViewers : 0;
  const formatPercentValue = (value) => `${(Number(value || 0) * 100).toFixed(1).replace(/\.0$/, '')}%`;
  const outreachValue = (value) => (hasOutreachData ? formatNumber(value) : EMPTY_VALUE);
  const jobValue = (value) => (hasJobData ? formatNumber(value) : EMPTY_VALUE);
  const unlinkedCount = projects.filter((project) => !project.job).length;
  const activeSummary =
    summary.activeProjects === summary.projects
      ? {
          value: summary.projects,
          label: `${summary.projects === 1 ? 'outreach project' : 'outreach projects'}, all active`,
        }
      : {
          value: summary.activeProjects,
          label: `of ${formatNumber(summary.projects)} projects active`,
        };
  const stages = [
    {
      value: outreachValue(summary.contacts),
      label: 'Candidates contacted',
      sub: hasOutreachData ? `across ${pluralize(summary.projects || 0, 'project')}` : 'Outreach data is updating',
    },
    {
      value: outreachValue(summary.anyLinkClickers),
      label: 'Clicked a link in an email',
      sub: hasOutreachData ? 'unique candidates who clicked any link' : 'Outreach data is updating',
      chip: hasOutreachData ? `${formatPercentValue(summary.anyLinkCtr)} any-link CTR` : null,
    },
    {
      value: jobValue(summary.uniqueJobViewers),
      label: 'Unique job visitors',
      sub: hasJobData ? `${formatNumber(summary.jobPageViews)} page views` : 'Job activity is updating',
      chip: hasJobData ? 'on your job board' : null,
    },
    {
      value: jobValue(summary.jobApplyClicks),
      label: 'Apply button clicks',
      sub: hasJobData ? 'intent to apply, not applications' : 'Job activity is updating',
      chip: hasJobData ? `${formatPercentValue(applyRate)} of visitors` : null,
    },
  ];

  return (
    <section className="TalentAttraction__journey" aria-label="Outreach to job board">
      <div className="TalentAttraction__journey-heading">
        <h2>Outreach → job board</h2>
        <span>
          {pluralize(summary.projects || 0, 'project')} · {rangeLabel}
        </span>
      </div>
      <div className="TalentAttraction__journey-stages">
        {stages.map((stage) => (
          <div className="TalentAttraction__journey-stage" key={stage.label}>
            <div className="TalentAttraction__metric-chip-row">
              {stage.chip && <span className="TalentAttraction__metric-chip">{stage.chip}</span>}
            </div>
            <strong>{stage.value}</strong>
            <h3>{stage.label}</h3>
            <p>{stage.sub}</p>
          </div>
        ))}
      </div>
      <div className="TalentAttraction__journey-footer">
        <div className="TalentAttraction__journey-stats">
          <span>
            <strong>{formatNumber(activeSummary.value)}</strong> {activeSummary.label}
          </span>
          <span>
            <strong>{formatNumber(summary.mappedJobs)}</strong> {summary.mappedJobs === 1 ? 'job' : 'jobs'} linked
          </span>
          <span>
            <strong>{formatNumber(unlinkedCount)}</strong> {unlinkedCount === 1 ? 'project' : 'projects'} not linked yet
          </span>
        </div>
        <p>
          Any-link CTR is the share of contacted candidates who clicked at least one link in an outreach email. Job
          visits and applies count traffic from every source, so a role can gather visitors with no outreach clicks at
          all.
        </p>
      </div>
    </section>
  );
};

export const UnlinkedProjectsBanner = ({ count, active, onReview }) => {
  if (!count) return null;

  return (
    <section className="TalentAttraction__unlinked-banner">
      <LinkIcon aria-hidden="true" />
      <div>
        <strong>{pluralize(count, 'project isn’t', 'projects aren’t')} linked to a job</strong>
        <p>Link {count === 1 ? 'it' : 'them'} to see how candidates behaved on your job board.</p>
      </div>
      <Button variant="tertiary" height="32px" onClick={onReview}>
        {active ? 'Clear unlinked filter' : 'Review unlinked'}
      </Button>
    </section>
  );
};

export const ProjectsList = ({
  projects,
  linkedJobs,
  query,
  companyFilter,
  unlinkedOnly,
  contactedProjectsOnly,
  hasHiddenProjects,
  hasOutreachData,
  hasJobData,
  networkSlug,
  projectRoute,
  onQueryChange,
  onCompanyChange,
  onExportProjects,
  onLinkProject,
  onContactedProjectsOnlyChange,
  exportCount,
  exportRangeLabel,
  formatDate,
  formatNumber,
  formatPercent,
}) => {
  const metricsByJobId = useMemo(() => new Map(linkedJobs.map((job) => [String(job.id), job])), [linkedJobs]);
  const companyOptions = useMemo(() => {
    const companies = new Map();
    projects.forEach((project) => {
      if (!project.job) return;
      const key = companyKey(project.job.company);
      companies.set(key, project.job.company?.name || 'Company unavailable');
    });
    const sortedCompanies = Array.from(companies, ([value, label]) => ({ value, label })).sort(compareLabels);
    return [{ label: 'All companies', value: ALL_COMPANIES }, ...sortedCompanies];
  }, [projects]);
  const selectedCompany = companyOptions.some(({ value }) => value === companyFilter) ? companyFilter : ALL_COMPANIES;
  useEffect(() => {
    if (companyFilter !== selectedCompany) onCompanyChange(selectedCompany);
  }, [companyFilter, onCompanyChange, selectedCompany]);
  const normalizedQuery = query.trim().toLocaleLowerCase();
  const visibleProjects = projects.filter((project) => {
    if (unlinkedOnly && project.job) return false;
    if (selectedCompany !== ALL_COMPANIES && companyKey(project.job?.company) !== selectedCompany) return false;
    if (!normalizedQuery) return true;
    return [project.name, displayProjectName(project.name), project.job?.title]
      .filter(Boolean)
      .some((value) => value.toLocaleLowerCase().includes(normalizedQuery));
  });
  const visibleJobIds = new Set(
    visibleProjects.filter((project) => project.job).map((project) => String(project.job.id)),
  );
  const visibleJobMetrics = Array.from(visibleJobIds, (jobId) => metricsByJobId.get(jobId)).filter(Boolean);
  const visibleTotals = {
    contacts: visibleProjects.reduce((total, project) => total + Number(project.contacts || 0), 0),
    uniqueViewers: visibleJobMetrics.reduce((total, job) => total + Number(job.uniqueViewers || 0), 0),
    applyClicks: visibleJobMetrics.reduce((total, job) => total + Number(job.applyClicks || 0), 0),
  };
  const selectedOption = companyOptions.find(({ value }) => value === selectedCompany);
  const exportTooltip = exportCount
    ? `Downloads all ${formatNumber(exportCount)} ${
        exportCount === 1 ? 'project' : 'projects'
      } for ${exportRangeLabel} as a CSV. Table filters do not apply.`
    : 'No projects are available to export for this launch-date range.';
  const contactedFilterTooltip = hasOutreachData
    ? 'Shows projects that have emailed at least one candidate. Turn off to also see projects that haven’t contacted anyone yet.'
    : 'Contacted filtering is unavailable while outreach results are still updating.';

  return (
    <section className="TalentAttraction__projects" id="outreach-projects">
      <div className="TalentAttraction__projects-toolbar">
        <div>
          <h2>Projects and their jobs</h2>
          <p>One row per outreach project, with the job it points to.</p>
        </div>
        <div className="TalentAttraction__project-filters">
          <div className="TalentAttraction__contacted-only-toggle">
            <span id="talent-attraction-contacted-projects-only-label">Contacted projects only</span>
            <Tooltip
              content={contactedFilterTooltip}
              openWith={['hover', 'focus']}
              position="top"
              trigger={
                <button aria-label="About this filter" className="TalentAttraction__filter-help" type="button">
                  <HelpCircle aria-hidden="true" />
                </button>
              }
            />
            <Toggle
              aria-labelledby="talent-attraction-contacted-projects-only-label"
              checked={hasOutreachData && contactedProjectsOnly}
              disabled={!hasOutreachData}
              onChange={() => onContactedProjectsOnlyChange(!contactedProjectsOnly)}
            />
          </div>
          <div className="TalentAttraction__project-search">
            <span className="TalentAttraction__visually-hidden">Search projects or jobs</span>
            <Search aria-hidden="true" />
            <Input
              aria-label="Search projects or jobs"
              id="talent-attraction-project-search"
              size="small"
              variant="search"
              placeholder="Search projects or jobs"
              value={query}
              onChange={(event) => onQueryChange(event.target.value)}
            />
          </div>
          <div className="TalentAttraction__company-select">
            <Select
              aria-label="Company"
              size="small"
              options={companyOptions}
              value={selectedOption}
              onChange={(option) => onCompanyChange(option?.value || ALL_COMPANIES)}
            />
          </div>
          <div className="TalentAttraction__export-action">
            <Tooltip
              content={exportTooltip}
              openWith={['hover', 'focus']}
              position="top-end"
              sx={{ maxWidth: '260px !important' }}
              trigger={
                <Button
                  aria-label="Export all projects as CSV"
                  aria-disabled={!exportCount}
                  className="TalentAttraction__export-button"
                  icon={Download}
                  onClick={exportCount ? onExportProjects : undefined}
                  size="small"
                  variant="secondary"
                >
                  Export
                </Button>
              }
            />
          </div>
        </div>
      </div>

      <div className="TalentAttraction__project-card">
        <div className="TalentAttraction__project-header">
          <span className="TalentAttraction__project-column">Project</span>
          {COLUMN_DEFINITIONS.map((column) => (
            <span className="TalentAttraction__numeric-column" key={column.key}>
              <Tooltip
                content={column.tooltip}
                openWith={['hover', 'focus']}
                position="top"
                trigger={
                  <button className="TalentAttraction__column-help" type="button">
                    {column.label}
                  </button>
                }
              />
            </span>
          ))}
        </div>

        {visibleProjects.length === 0 && (
          <div className="TalentAttraction__empty-row">
            {projects.length === 0 && hasHiddenProjects
              ? 'No contacted projects yet. Turn off “Contacted projects only” to see projects that haven’t contacted anyone.'
              : 'No projects match these filters. Adjust the search or company filter to see more results.'}
          </div>
        )}

        {visibleProjects.map((project) => {
          const jobMetric = project.job ? metricsByJobId.get(String(project.job.id)) : null;
          const jobLink = project.job?.url || `/networks/${networkSlug}/jobs?visibility=visible`;
          const peopleFinderRoute = projectRoute(project);
          const values = [
            hasOutreachData ? formatNumber(project.contacts) : EMPTY_VALUE,
            hasOutreachData ? formatPercent(project.anyLinkCtr) : EMPTY_VALUE,
            hasJobData && jobMetric ? formatNumber(jobMetric.uniqueViewers) : EMPTY_VALUE,
            hasJobData && jobMetric ? formatNumber(jobMetric.applyClicks) : EMPTY_VALUE,
          ];

          return (
            <article className="TalentAttraction__project-row" key={project.id}>
              <div className="TalentAttraction__project-column">
                <div className="TalentAttraction__project-name-line">
                  {peopleFinderRoute ? (
                    <Link
                      className="TalentAttraction__project-link"
                      to={peopleFinderRoute}
                      aria-label={`Open ${displayProjectName(project.name)} in People Finder`}
                    >
                      {displayProjectName(project.name)}
                    </Link>
                  ) : (
                    <span>{displayProjectName(project.name)}</span>
                  )}
                  {!project.job && project.mappingSource !== 'exact' && (
                    <button
                      className="TalentAttraction__link-job-button"
                      type="button"
                      onClick={() => onLinkProject(project)}
                    >
                      <LinkIcon aria-hidden="true" />
                      Link a job
                    </button>
                  )}
                </div>
                <div className="TalentAttraction__project-meta">
                  <span
                    className={`TalentAttraction__link-status TalentAttraction__link-status--${
                      project.job ? 'linked' : 'unlinked'
                    }`}
                    aria-label={project.job ? 'Linked to a job' : 'Not linked to a job'}
                  />
                  {project.job && (
                    <>
                      <ProfilePicture
                        variant="square"
                        width="18px"
                        height="18px"
                        name={project.job.company?.name || 'Company'}
                        imageUrl={project.job.company?.logoUrl || ''}
                      />
                      <a
                        className="TalentAttraction__job-link"
                        href={jobLink}
                        target="_blank"
                        rel="noopener noreferrer"
                        title={`${project.job.company?.name || 'Company unavailable'} · ${linkedLabel(
                          project.mappingSource,
                        )}`}
                      >
                        {project.job.title}
                      </a>
                    </>
                  )}
                  <span>
                    {project.firstSentAt ? `First email ${formatDate(project.firstSentAt)}` : 'Not started yet'}
                  </span>
                  {project.job && project.mappingSource !== 'exact' && (
                    <button
                      className="TalentAttraction__change-link-button"
                      type="button"
                      onClick={() => onLinkProject(project)}
                    >
                      Change
                    </button>
                  )}
                </div>
              </div>
              <div className="TalentAttraction__project-values">
                {COLUMN_DEFINITIONS.map((column, index) => (
                  <div
                    className={`TalentAttraction__numeric-column${
                      index === 0 ? ' TalentAttraction__numeric-column--strong' : ''
                    }${values[index] === EMPTY_VALUE ? ' TalentAttraction__numeric-column--empty' : ''}`}
                    data-label={column.label}
                    key={column.key}
                  >
                    {values[index]}
                  </div>
                ))}
              </div>
            </article>
          );
        })}

        <div className="TalentAttraction__project-footer">
          <span>
            {pluralize(visibleProjects.length, 'project')} · {formatNumber(visibleJobIds.size)} linked{' '}
            {visibleJobIds.size === 1 ? 'job' : 'jobs'}
          </span>
          <span>
            Totals: {hasOutreachData ? formatNumber(visibleTotals.contacts) : EMPTY_VALUE} contacted ·{' '}
            {hasJobData ? formatNumber(visibleTotals.uniqueViewers) : EMPTY_VALUE} visitors ·{' '}
            {hasJobData ? formatNumber(visibleTotals.applyClicks) : EMPTY_VALUE} applies
          </span>
        </div>
      </div>
      <p className="TalentAttraction__project-caveat">Each job can be linked to only one People Finder project.</p>
    </section>
  );
};

DataState.propTypes = {
  title: PropTypes.string.isRequired,
  children: PropTypes.node.isRequired,
  // Defaults are provided in the function signature.
  // eslint-disable-next-line react/require-default-props
  action: PropTypes.node,
  // eslint-disable-next-line react/require-default-props
  variant: PropTypes.oneOf(['default', 'error']),
};

OutreachJourney.propTypes = {
  summary: PropTypes.object.isRequired,
  projects: PropTypes.arrayOf(PropTypes.object).isRequired,
  hasOutreachData: PropTypes.bool.isRequired,
  hasJobData: PropTypes.bool.isRequired,
  rangeLabel: PropTypes.string.isRequired,
  formatNumber: PropTypes.func.isRequired,
};

UnlinkedProjectsBanner.propTypes = {
  count: PropTypes.number.isRequired,
  active: PropTypes.bool.isRequired,
  onReview: PropTypes.func.isRequired,
};

ProjectsList.propTypes = {
  projects: PropTypes.arrayOf(PropTypes.object).isRequired,
  linkedJobs: PropTypes.arrayOf(PropTypes.object).isRequired,
  query: PropTypes.string.isRequired,
  companyFilter: PropTypes.string.isRequired,
  unlinkedOnly: PropTypes.bool.isRequired,
  contactedProjectsOnly: PropTypes.bool.isRequired,
  hasHiddenProjects: PropTypes.bool.isRequired,
  hasOutreachData: PropTypes.bool.isRequired,
  hasJobData: PropTypes.bool.isRequired,
  networkSlug: PropTypes.string.isRequired,
  projectRoute: PropTypes.func.isRequired,
  onQueryChange: PropTypes.func.isRequired,
  onCompanyChange: PropTypes.func.isRequired,
  onExportProjects: PropTypes.func.isRequired,
  onLinkProject: PropTypes.func.isRequired,
  onContactedProjectsOnlyChange: PropTypes.func.isRequired,
  exportCount: PropTypes.number.isRequired,
  exportRangeLabel: PropTypes.string.isRequired,
  formatDate: PropTypes.func.isRequired,
  formatNumber: PropTypes.func.isRequired,
  formatPercent: PropTypes.func.isRequired,
};
